// src/hooks/useForm.js
import { useState } from 'react';

/**
 * Custom hook for handling form state and validation
 * @param {Object} initialValues - Initial form field values
 * @param {Function} validate - Validation function (optional)
 * @param {Function} onSubmit - Function to call on form submission
 * @returns {Object} Form handling methods and state
 */
const useForm = (initialValues, validate, onSubmit) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setValues(prevValues => ({
      ...prevValues,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files?.[0] || null : value
    }));
    
    // Clear error for this field when it changes
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: undefined
      }));
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate if validation function is provided
    if (validate) {
      const validationErrors = validate(values);
      setErrors(validationErrors);
      
      // Don't submit if there are validation errors
      if (Object.keys(validationErrors).length > 0) {
        return;
      }
    }
    
    setIsSubmitting(true);
    
    try {
      await onSubmit(values);
      setIsSubmitted(true);
      
      // Reset form after successful submission
      setValues(initialValues);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ form: error.message || 'An error occurred during submission' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Reset form to initial state
  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
    setIsSubmitted(false);
  };
  
  // Set a specific field value programmatically
  const setFieldValue = (field, value) => {
    setValues(prevValues => ({
      ...prevValues,
      [field]: value
    }));
  };
  
  return {
    values,
    errors,
    isSubmitting,
    isSubmitted,
    handleChange,
    handleSubmit,
    resetForm,
    setFieldValue,
    setErrors
  };
};

export default useForm;
